import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { getTradeJournal, saveTradeEntry, deleteTradeEntry, exportToCSV, getValidatedTradeById, markValidatedTradeAsUsed, cleanupExpiredValidations, linkTradeToPatternAlert, getPatternAlerts, importPaperTradesIntoJournal, updateTradeEntry } from '@/utils/localStorage';
import { calculateJournalStats } from '@/utils/calculations';
import { toast } from 'sonner';
import { Plus, Download, Trash2, TrendingUp, TrendingDown, BookOpen, Shield, AlertCircle, Bot, Pencil } from 'lucide-react';
import { format } from 'date-fns';
import { useAuth } from '@/contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TradeJournal() {
  const { token } = useAuth();
  const [trades, setTrades] = useState([]);
  const [stats, setStats] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedTrade, setSelectedTrade] = useState(null);
  const [reflectionTrade, setReflectionTrade] = useState(null);
  const [reflectionDraft, setReflectionDraft] = useState({ emotion: '', mistake: '', lesson: '', notes: '' });

  const openReflection = (trade) => {
    setReflectionTrade(trade);
    setReflectionDraft({
      emotion: trade.emotion || '',
      mistake: trade.mistake || '',
      lesson: trade.lesson || '',
      notes: trade.notes || '',
    });
  };

  const saveReflection = () => {
    if (!reflectionTrade) return;
    updateTradeEntry(reflectionTrade.id, reflectionDraft);
    toast.success('Reflection saved');
    setReflectionTrade(null);
    loadTrades();
  };
  
  const [newTrade, setNewTrade] = useState({
    asset: '',
    setupType: '',
    timeframe: '',
    entryPrice: '',
    exitPrice: '',
    stopLoss: '',
    takeProfit: '',
    result: '',
    emotion: '',
    mistake: '',
    notes: '',
    chartScreenshot: null,
    ruleViolation: false,
    preTradeId: '',
    patternAlertId: ''
  });
  
  useEffect(() => {
    autoImportAndLoad();
    cleanupExpiredValidations();
    // Re-import every 60s so newly-closed paper trades land automatically
    const interval = setInterval(autoImportAndLoad, 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const autoImportAndLoad = async () => {
    if (!token) {
      loadTrades();
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/paper-trading/trades?limit=200`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const paperTrades = await res.json();
        const imported = importPaperTradesIntoJournal(paperTrades);
        if (imported > 0) {
          toast.success(`${imported} paper trade${imported > 1 ? 's' : ''} auto-imported to journal`);
        }
      }
    } catch {
      // silent — user can still see existing journal entries
    }
    loadTrades();
  };
  
  const loadTrades = () => {
    const journalData = getTradeJournal();
    setTrades(journalData);
    setStats(calculateJournalStats(journalData));
  };
  
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size should be less than 5MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewTrade(prev => ({ ...prev, chartScreenshot: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };
  
  const handleSaveTrade = () => {
    if (!newTrade.asset || !newTrade.result || !newTrade.emotion) {
      toast.error('Please fill required fields: Asset, Result, and Emotion');
      return;
    }
    
    // Calculate R:R if prices are available
    let riskRewardRatio = null;
    if (newTrade.entryPrice && newTrade.stopLoss && newTrade.takeProfit) {
      const entry = parseFloat(newTrade.entryPrice);
      const sl = parseFloat(newTrade.stopLoss);
      const tp = parseFloat(newTrade.takeProfit);
      const risk = Math.abs(entry - sl);
      const reward = Math.abs(tp - entry);
      riskRewardRatio = risk > 0 ? (reward / risk).toFixed(2) : null;
    }
    
    // Validate Pre-Trade ID
    let isPlanned = false;
    let isUnplanned = false;
    
    if (newTrade.preTradeId && newTrade.preTradeId.trim()) {
      const validatedTrade = getValidatedTradeById(newTrade.preTradeId.trim());
      
      if (!validatedTrade) {
        toast.error('Invalid Pre-Trade ID. This trade will be marked as "Unplanned".');
        isUnplanned = true;
      } else if (validatedTrade.expired) {
        toast.error('Pre-Trade ID has expired (>24hrs). This trade will be marked as "Unplanned".');
        isUnplanned = true;
      } else if (validatedTrade.used) {
        toast.error('Pre-Trade ID already used. This trade will be marked as "Unplanned".');
        isUnplanned = true;
      } else {
        // Valid Pre-Trade ID
        isPlanned = true;
        markValidatedTradeAsUsed(newTrade.preTradeId.trim());
        toast.success('Trade logged as PLANNED with validated Pre-Trade ID');
      }
    } else {
      // No Pre-Trade ID provided
      isUnplanned = true;
      toast.warning('No Pre-Trade ID provided. Trade marked as "Unplanned".');
    }
    
    try {
      const savedTrade = saveTradeEntry({ 
        ...newTrade, 
        riskRewardRatio,
        isPlanned,
        isUnplanned
      });
      
      // Link to pattern alert if provided
      if (newTrade.patternAlertId && newTrade.patternAlertId.trim()) {
        linkTradeToPatternAlert(newTrade.patternAlertId.trim(), savedTrade.id, newTrade.result);
        toast.info('Trade linked to pattern alert for tracking');
      }
      
      setNewTrade({
        asset: '',
        setupType: '',
        timeframe: '',
        entryPrice: '',
        exitPrice: '',
        stopLoss: '',
        takeProfit: '',
        result: '',
        emotion: '',
        mistake: '',
        notes: '',
        chartScreenshot: null,
        ruleViolation: false,
        preTradeId: '',
        patternAlertId: ''
      });
      setIsDialogOpen(false);
      loadTrades();
    } catch (error) {
      toast.error('Failed to save trade');
    }
  };
  
  const handleDeleteTrade = (id) => {
    if (window.confirm('Are you sure you want to delete this trade?')) {
      try {
        deleteTradeEntry(id);
        toast.success('Trade deleted');
        loadTrades();
        setSelectedTrade(null);
      } catch (error) {
        toast.error('Failed to delete trade');
      }
    }
  };
  
  const handleExportCSV = () => {
    if (trades.length === 0) {
      toast.error('No trades to export');
      return;
    }
    exportToCSV(trades, `trade-journal-${format(new Date(), 'yyyy-MM-dd')}.csv`);
    toast.success('Journal exported to CSV');
  };
  
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter text-foreground mb-2">
            Trade Journal
          </h1>
          <p className="text-muted-foreground font-medium">
            Log your trades and track performance. Learn from patterns and mistakes.
          </p>
        </div>
        
        <div className="flex space-x-3">
          <Button
            onClick={handleExportCSV}
            data-testid="export-csv-btn"
            variant="outline"
            className="border-border hover:bg-accent"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-trade-btn" className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold">
                <Plus className="w-4 h-4 mr-2" />
                Add Trade
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl bg-card border-border/40 max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold">Log New Trade</DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="asset" className="font-semibold mb-2 block">Asset *</Label>
                    <Input
                      id="asset"
                      data-testid="trade-asset-input"
                      value={newTrade.asset}
                      onChange={(e) => setNewTrade({ ...newTrade, asset: e.target.value })}
                      placeholder="e.g., EURUSD"
                      className="h-11 bg-secondary/20 border-border/50"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="setupType" className="font-semibold mb-2 block">Setup Type</Label>
                    <Select value={newTrade.setupType} onValueChange={(value) => setNewTrade({ ...newTrade, setupType: value })}>
                      <SelectTrigger data-testid="trade-setup-select" className="h-11 bg-secondary/20 border-border/50">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="breakout">Breakout</SelectItem>
                        <SelectItem value="pullback">Pullback</SelectItem>
                        <SelectItem value="reversal">Reversal</SelectItem>
                        <SelectItem value="continuation">Continuation</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="timeframe" className="font-semibold mb-2 block">Timeframe</Label>
                    <Select value={newTrade.timeframe} onValueChange={(value) => setNewTrade({ ...newTrade, timeframe: value })}>
                      <SelectTrigger data-testid="trade-timeframe-select" className="h-11 bg-secondary/20 border-border/50">
                        <SelectValue placeholder="Select timeframe" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="5m">5m</SelectItem>
                        <SelectItem value="15m">15m</SelectItem>
                        <SelectItem value="30m">30m</SelectItem>
                        <SelectItem value="1h">1h</SelectItem>
                        <SelectItem value="4h">4h</SelectItem>
                        <SelectItem value="1d">1D</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="patternAlertId" className="font-semibold mb-2 block">Pattern Alert ID</Label>
                    <Input
                      id="patternAlertId"
                      data-testid="pattern-alert-id-input"
                      value={newTrade.patternAlertId}
                      onChange={(e) => setNewTrade({ ...newTrade, patternAlertId: e.target.value })}
                      placeholder="PA-123456-ABC (optional)"
                      className="h-11 bg-secondary/20 border-border/50 font-mono text-sm"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="entryPrice" className="font-semibold mb-2 block">Entry Price</Label>
                    <Input
                      id="entryPrice"
                      data-testid="trade-entry-input"
                      type="number"
                      step="any"
                      value={newTrade.entryPrice}
                      onChange={(e) => setNewTrade({ ...newTrade, entryPrice: e.target.value })}
                      placeholder="0.00"
                      className="h-11 bg-secondary/20 border-border/50 font-mono"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="exitPrice" className="font-semibold mb-2 block">Exit Price</Label>
                    <Input
                      id="exitPrice"
                      data-testid="trade-exit-input"
                      type="number"
                      step="any"
                      value={newTrade.exitPrice}
                      onChange={(e) => setNewTrade({ ...newTrade, exitPrice: e.target.value })}
                      placeholder="0.00"
                      className="h-11 bg-secondary/20 border-border/50 font-mono"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="stopLoss" className="font-semibold mb-2 block">Stop Loss</Label>
                    <Input
                      id="stopLoss"
                      data-testid="trade-sl-input"
                      type="number"
                      step="any"
                      value={newTrade.stopLoss}
                      onChange={(e) => setNewTrade({ ...newTrade, stopLoss: e.target.value })}
                      placeholder="0.00"
                      className="h-11 bg-secondary/20 border-border/50 font-mono"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="takeProfit" className="font-semibold mb-2 block">Take Profit</Label>
                    <Input
                      id="takeProfit"
                      data-testid="trade-tp-input"
                      type="number"
                      step="any"
                      value={newTrade.takeProfit}
                      onChange={(e) => setNewTrade({ ...newTrade, takeProfit: e.target.value })}
                      placeholder="0.00"
                      className="h-11 bg-secondary/20 border-border/50 font-mono"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="result" className="font-semibold mb-2 block">Result *</Label>
                    <Select value={newTrade.result} onValueChange={(value) => setNewTrade({ ...newTrade, result: value })}>
                      <SelectTrigger data-testid="trade-result-select" className="h-11 bg-secondary/20 border-border/50">
                        <SelectValue placeholder="Win or Loss" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="win">Win</SelectItem>
                        <SelectItem value="loss">Loss</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="emotion" className="font-semibold mb-2 block">Emotion *</Label>
                    <Select value={newTrade.emotion} onValueChange={(value) => setNewTrade({ ...newTrade, emotion: value })}>
                      <SelectTrigger data-testid="trade-emotion-select" className="h-11 bg-secondary/20 border-border/50">
                        <SelectValue placeholder="Select emotion" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="calm">Calm</SelectItem>
                        <SelectItem value="FOMO">FOMO</SelectItem>
                        <SelectItem value="revenge">Revenge</SelectItem>
                        <SelectItem value="bored">Bored</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="p-4 rounded-md bg-primary/5 border border-primary/30">
                  <div className="flex items-start space-x-2 mb-3">
                    <AlertCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <div>
                      <Label htmlFor="preTradeId" className="font-semibold mb-1 block">Pre-Trade ID (Required for Planned Trades)</Label>
                      <p className="text-xs text-muted-foreground mb-2">
                        Enter the Pre-Trade ID from the Trade Validator. Without a valid ID, this trade will be marked as "Unplanned".
                      </p>
                    </div>
                  </div>
                  <Input
                    id="preTradeId"
                    data-testid="pretrade-id-input"
                    value={newTrade.preTradeId}
                    onChange={(e) => setNewTrade({ ...newTrade, preTradeId: e.target.value })}
                    placeholder="e.g., PT-1234567890-ABC123"
                    className="h-11 bg-secondary/20 border-border/50 font-mono"
                  />
                </div>
                
                <div>
                  <Label htmlFor="mistake" className="font-semibold mb-2 block">Mistake (Optional)</Label>
                  <Input
                    id="mistake"
                    data-testid="trade-mistake-input"
                    value={newTrade.mistake}
                    onChange={(e) => setNewTrade({ ...newTrade, mistake: e.target.value })}
                    placeholder="e.g., Entered too early"
                    className="h-11 bg-secondary/20 border-border/50"
                  />
                </div>
                
                <div>
                  <Label htmlFor="notes" className="font-semibold mb-2 block">Notes</Label>
                  <Textarea
                    id="notes"
                    data-testid="trade-notes-input"
                    value={newTrade.notes}
                    onChange={(e) => setNewTrade({ ...newTrade, notes: e.target.value })}
                    placeholder="Additional notes about this trade..."
                    rows={3}
                    className="bg-secondary/20 border-border/50 resize-none"
                  />
                </div>
                
                <div>
                  <Label htmlFor="chartUpload" className="font-semibold mb-2 block">Chart Screenshot</Label>
                  <Input
                    type="file"
                    id="chartUpload"
                    data-testid="trade-chart-input"
                    accept="image/*"
                    onChange={handleFileUpload}
                    className="h-11 bg-secondary/20 border-border/50"
                  />
                  {newTrade.chartScreenshot && (
                    <div className="mt-2 rounded-md overflow-hidden border border-border/40">
                      <img src={newTrade.chartScreenshot} alt="Chart" className="w-full h-auto" />
                    </div>
                  )}
                </div>
                
                <div className="flex items-center justify-between py-3 px-4 rounded-md bg-destructive/10 border border-destructive/30">
                  <div>
                    <Label htmlFor="ruleViolation" className="font-semibold cursor-pointer">
                      Rule Violation Trade
                    </Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      Check this if this trade broke your trading plan rules
                    </p>
                  </div>
                  <Switch
                    id="ruleViolation"
                    data-testid="rule-violation-toggle"
                    checked={newTrade.ruleViolation}
                    onCheckedChange={(checked) => setNewTrade({ ...newTrade, ruleViolation: checked })}
                  />
                </div>
                
                <Button
                  onClick={handleSaveTrade}
                  data-testid="save-trade-btn"
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold h-11 mt-4"
                >
                  Save Trade
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="p-6 border-border/40 bg-card">
            <p className="text-sm text-muted-foreground mb-1">Total Trades</p>
            <p className="text-3xl font-black font-mono" data-testid="total-trades">{stats.totalTrades}</p>
          </Card>
          
          <Card className="p-6 border-border/40 bg-card">
            <p className="text-sm text-muted-foreground mb-1">Win Rate</p>
            <p className="text-3xl font-black font-mono text-success" data-testid="win-rate">{stats.winRate}%</p>
          </Card>
          
          <Card className="p-6 border-border/40 bg-card">
            <p className="text-sm text-muted-foreground mb-1">Avg R:R</p>
            <p className="text-3xl font-black font-mono text-primary" data-testid="avg-rr">{stats.avgRR}</p>
          </Card>
          
          <Card className="p-6 border-border/40 bg-card">
            <p className="text-sm text-muted-foreground mb-1">Common Mistake</p>
            <p className="text-lg font-semibold truncate" data-testid="common-mistake">{stats.mostCommonMistake}</p>
          </Card>
        </div>
      )}
      
      <Card className="border-border/40 bg-card">
        <div className="overflow-x-auto">
          {trades.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">
              <BookOpen className="w-16 h-16 mx-auto mb-4 opacity-20" />
              <p className="font-medium">No trades logged yet. Click "Add Trade" to start your journal.</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="border-b border-border/40">
                <tr className="text-left">
                  <th className="p-4 font-bold text-sm text-muted-foreground">Date</th>
                  <th className="p-4 font-bold text-sm text-muted-foreground">Asset</th>
                  <th className="p-4 font-bold text-sm text-muted-foreground">Setup</th>
                  <th className="p-4 font-bold text-sm text-muted-foreground">Result</th>
                  <th className="p-4 font-bold text-sm text-muted-foreground">R:R</th>
                  <th className="p-4 font-bold text-sm text-muted-foreground">Emotion</th>
                  <th className="p-4 font-bold text-sm text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {trades.map((trade) => (
                  <tr
                    key={trade.id}
                    data-testid={`trade-row-${trade.id}`}
                    className="border-b border-border/20 hover:bg-accent/50 transition-colors cursor-pointer"
                    onClick={() => setSelectedTrade(selectedTrade?.id === trade.id ? null : trade)}
                  >
                    <td className="p-4 font-mono text-sm">
                      {format(new Date(trade.timestamp), 'MMM dd, yyyy')}
                    </td>
                    <td className="p-4 font-semibold">
                      <div className="flex flex-col space-y-1">
                        <span>{trade.asset}</span>
                        <div className="flex flex-wrap gap-1">
                          {trade.source === 'paper_trading' && (
                            <span
                              className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-primary/15 text-primary border border-primary/40 flex items-center"
                              data-testid={`auto-imported-badge-${trade.id}`}
                            >
                              <Bot className="w-3 h-3 mr-1" />
                              AUTO-IMPORTED
                            </span>
                          )}
                          {trade.ruleViolation && (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-destructive/20 text-destructive border border-destructive/50 flex items-center">
                              <Shield className="w-3 h-3 mr-1" />
                              RULE VIOLATION
                            </span>
                          )}
                          {trade.isUnplanned && (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-warning/20 text-warning border border-warning/50 flex items-center">
                              <AlertCircle className="w-3 h-3 mr-1" />
                              UNPLANNED
                            </span>
                          )}
                          {trade.isPlanned && !trade.isUnplanned && (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-success/20 text-success border border-success/50">
                              ✓ PLANNED
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="p-4 text-sm capitalize">{trade.setupType || '-'}</td>
                    <td className="p-4">
                      <div className="flex items-center space-x-2">
                        {trade.result === 'win' ? (
                          <TrendingUp className="w-4 h-4 text-success" />
                        ) : (
                          <TrendingDown className="w-4 h-4 text-destructive" />
                        )}
                        <span className={`font-semibold capitalize ${
                          trade.result === 'win' ? 'text-success' : 'text-destructive'
                        }`}>
                          {trade.result}
                        </span>
                      </div>
                    </td>
                    <td className="p-4 font-mono font-semibold">{trade.riskRewardRatio || '-'}</td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        trade.emotion === 'calm' ? 'bg-success/20 text-success' :
                        trade.emotion === 'FOMO' ? 'bg-destructive/20 text-destructive' :
                        trade.emotion === 'revenge' ? 'bg-destructive/20 text-destructive' :
                        trade.emotion ? 'bg-warning/20 text-warning' :
                        'bg-muted/40 text-muted-foreground'
                      }`}>
                        {trade.emotion || '—'}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center space-x-1">
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            openReflection(trade);
                          }}
                          data-testid={`reflect-trade-${trade.id}`}
                          variant="ghost"
                          size="sm"
                          className="hover:bg-primary/10 hover:text-primary"
                          title={trade.emotion || trade.lesson ? 'Edit reflection' : 'Add reflection'}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteTrade(trade.id);
                          }}
                          data-testid={`delete-trade-${trade.id}`}
                          variant="ghost"
                          size="sm"
                          className="hover:bg-destructive/20 hover:text-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        
        {selectedTrade && (
          <div className="p-6 border-t border-border/40 bg-secondary/20">
            <h3 className="text-lg font-bold text-foreground mb-4">Trade Details</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                {selectedTrade.entryPrice && (
                  <div>
                    <p className="text-sm text-muted-foreground">Entry Price</p>
                    <p className="font-mono font-semibold">{selectedTrade.entryPrice}</p>
                  </div>
                )}
                {selectedTrade.exitPrice && (
                  <div>
                    <p className="text-sm text-muted-foreground">Exit Price</p>
                    <p className="font-mono font-semibold">{selectedTrade.exitPrice}</p>
                  </div>
                )}
                {selectedTrade.stopLoss && (
                  <div>
                    <p className="text-sm text-muted-foreground">Stop Loss</p>
                    <p className="font-mono font-semibold">{selectedTrade.stopLoss}</p>
                  </div>
                )}
                {selectedTrade.takeProfit && (
                  <div>
                    <p className="text-sm text-muted-foreground">Take Profit</p>
                    <p className="font-mono font-semibold">{selectedTrade.takeProfit}</p>
                  </div>
                )}
                {selectedTrade.mistake && (
                  <div>
                    <p className="text-sm text-muted-foreground">Mistake</p>
                    <p className="font-semibold text-destructive">{selectedTrade.mistake}</p>
                  </div>
                )}
                {selectedTrade.notes && (
                  <div>
                    <p className="text-sm text-muted-foreground">Notes</p>
                    <p className="text-foreground">{selectedTrade.notes}</p>
                  </div>
                )}
              </div>
              
              {selectedTrade.chartScreenshot && (
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Chart Screenshot</p>
                  <div className="rounded-md overflow-hidden border border-border/40">
                    <img src={selectedTrade.chartScreenshot} alt="Trade chart" className="w-full h-auto" />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </Card>

      {/* Reflection editor dialog */}
      <Dialog open={!!reflectionTrade} onOpenChange={(open) => !open && setReflectionTrade(null)}>
        <DialogContent className="bg-card border-border/40 max-w-lg" data-testid="reflection-dialog">
          <DialogHeader>
            <DialogTitle>
              {reflectionTrade?.asset} —{' '}
              <span className={reflectionTrade?.result === 'win' ? 'text-success' : reflectionTrade?.result === 'loss' ? 'text-destructive' : ''}>
                {(reflectionTrade?.result || '').toUpperCase() || 'TRADE'}
              </span>
            </DialogTitle>
          </DialogHeader>

          {reflectionTrade && (
            <div className="space-y-4">
              {/* Read-only summary */}
              <div className="grid grid-cols-2 gap-3 text-xs bg-secondary/30 rounded p-3">
                <div data-testid="reflection-opened-at">
                  <span className="text-muted-foreground">Opened</span>{' '}
                  <span className="font-mono">
                    {reflectionTrade.openedAt
                      ? format(new Date(reflectionTrade.openedAt), 'MMM dd, HH:mm')
                      : '—'}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">Closed</span>{' '}
                  <span className="font-mono">{format(new Date(reflectionTrade.timestamp), 'MMM dd, HH:mm')}</span>
                </div>
                {reflectionTrade.openedAt && (
                  <div className="col-span-2" data-testid="reflection-hold-duration">
                    <span className="text-muted-foreground">Held for</span>{' '}
                    <span className="font-medium">
                      {(() => {
                        const ms = new Date(reflectionTrade.timestamp).getTime() - new Date(reflectionTrade.openedAt).getTime();
                        if (!isFinite(ms) || ms < 0) return '—';
                        const mins = Math.round(ms / 60000);
                        if (mins < 60) return `${mins} min`;
                        const hrs = Math.floor(mins / 60);
                        const rem = mins % 60;
                        if (hrs < 24) return rem ? `${hrs}h ${rem}m` : `${hrs}h`;
                        const days = Math.floor(hrs / 24);
                        const remH = hrs % 24;
                        return remH ? `${days}d ${remH}h` : `${days}d`;
                      })()}
                    </span>
                  </div>
                )}
                <div>
                  {(() => {
                    const pnl = reflectionTrade.pnl;
                    if (pnl == null) {
                      return (
                        <>
                          <span className="text-muted-foreground">P&amp;L</span>{' '}
                          <span className="font-mono">—</span>
                        </>
                      );
                    }
                    const isWin = pnl > 0;
                    const isLoss = pnl < 0;
                    const label = isWin ? 'Profit' : isLoss ? 'Loss' : 'Breakeven';
                    const color = isWin ? 'text-success' : isLoss ? 'text-destructive' : 'text-muted-foreground';
                    return (
                      <>
                        <span className={`font-semibold ${color}`}>{label}</span>{' '}
                        <span className={`font-mono font-semibold ${color}`}>
                          {`${isWin ? '+' : ''}$${pnl}`}
                        </span>
                      </>
                    );
                  })()}
                </div>
                <div>
                  <span className="text-muted-foreground">Entry</span>{' '}
                  <span className="font-mono">{reflectionTrade.entryPrice || '—'}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Exit</span>{' '}
                  <span className="font-mono">{reflectionTrade.exitPrice || '—'}</span>
                </div>
                {reflectionTrade.closeReason && (
                  <div className="col-span-2">
                    <span className="text-muted-foreground">Close reason</span>{' '}
                    <span className="font-medium">{reflectionTrade.closeReason}</span>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label>Emotion</Label>
                <Select
                  value={reflectionDraft.emotion}
                  onValueChange={(v) => setReflectionDraft((d) => ({ ...d, emotion: v }))}
                >
                  <SelectTrigger data-testid="reflection-emotion"><SelectValue placeholder="How did you feel?" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="calm">Calm</SelectItem>
                    <SelectItem value="confident">Confident</SelectItem>
                    <SelectItem value="anxious">Anxious</SelectItem>
                    <SelectItem value="FOMO">FOMO</SelectItem>
                    <SelectItem value="revenge">Revenge</SelectItem>
                    <SelectItem value="frustrated">Frustrated</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="reflection-mistake">Mistake (if any)</Label>
                <Input
                  id="reflection-mistake"
                  value={reflectionDraft.mistake}
                  onChange={(e) => setReflectionDraft((d) => ({ ...d, mistake: e.target.value }))}
                  placeholder="e.g. Entered too early, ignored higher-TF trend"
                  data-testid="reflection-mistake"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="reflection-lesson">Lesson learned</Label>
                <Input
                  id="reflection-lesson"
                  value={reflectionDraft.lesson}
                  onChange={(e) => setReflectionDraft((d) => ({ ...d, lesson: e.target.value }))}
                  placeholder="e.g. Followed trend correctly, wait for retest next time"
                  data-testid="reflection-lesson"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="reflection-notes">Notes</Label>
                <Textarea
                  id="reflection-notes"
                  rows={3}
                  value={reflectionDraft.notes}
                  onChange={(e) => setReflectionDraft((d) => ({ ...d, notes: e.target.value }))}
                  placeholder="Why you took the trade, context, anything else worth remembering."
                  data-testid="reflection-notes"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setReflectionTrade(null)} data-testid="reflection-cancel">
                  Cancel
                </Button>
                <Button onClick={saveReflection} className="bg-primary" data-testid="reflection-save">
                  Save Reflection
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}